import {
    Component,
    OnInit,
    inject,
    DestroyRef,
    Input,
    ViewChild
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
    FormBuilder,
    FormControl,
    FormGroup,
    Validators
} from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import {
    ButtonColor,
    ButtonSize,
    GridComponent,
    InlineMessageBoxType,
    TapestrySidenavService
} from '@predictiveindex/tapestry';
import { ColDef, GridReadyEvent } from 'ag-grid-community';
import {
    ProspectData,
    SellerToolsAccountTableData,
    ValidationResult
} from '../../../../core-data-model';
import { DemoAccountService } from '../../../../core-services';
import { FeatureFlags, FeatureFlagsService } from '../../../../system-services';
import { TranslationService } from '../../../../translations';
import { AddThirdPartyUsersModalComponent } from '../components/add-third-party-users-modal/add-third-party-users-modal.component';
import { DeleteAccountModalComponent } from '../components/delete-account-modal/delete-account-modal.component';
import { ActionMenuComponent } from './action-menu/action-menu.component';

type ProspectDemoAccountForm = FormGroup<{
    demoAccountName: FormControl<string>;
    users: FormGroup<{ [p: string]: FormControl<boolean> }>;
}>;

@Component({
    selector: 'prospect-demos',
    templateUrl: './prospect-demos.component.html',
    styleUrls: ['../seller-tools-base.component.less'],
    standalone: false
})
export class ProspectDemosComponent implements OnInit {
    @ViewChild('prospectDemoAccountsGrid')
    prospectDemoAccountsGrid: GridComponent;

    @Input()
    selectedDemoUserData: ProspectData[];

    @Input()
    openCreateDemo: boolean = false;

    private demoAccount = inject(DemoAccountService);
    private TR = inject(TranslationService);
    private flags = inject(FeatureFlagsService);
    private fb = inject(FormBuilder);
    private dialog = inject(MatDialog);
    private tapestrySidenav = inject(TapestrySidenavService);
    private destroyRef = inject(DestroyRef);

    columnDefs: ColDef[];
    form: ProspectDemoAccountForm;
    saving = false;
    showAddProspectDemoField = false;
    prospectDemoAccountData$ = this.demoAccount.demoAccounts$;

    protected readonly InlineMessageBoxType = InlineMessageBoxType;
    protected readonly ButtonSize = ButtonSize;
    protected readonly ButtonColor = ButtonColor;

    ngOnInit(): void {
        this.form = this.fb.group({
            demoAccountName: this.fb.control('', [Validators.required]),
            users: this.fb.record<boolean>({})
        });
        this.registerFormControls();
        this.showAddProspectDemoField = this.openCreateDemo;
        this.columnDefs = this.getColumnDefs();
        this.demoAccount
            .getProspectDemoAccounts()
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe((accounts) => {
                this.demoAccount.setDemoAccounts(accounts);
            });
    }

    gridReadyHandler(params: GridReadyEvent) {
        this.prospectDemoAccountsGrid.gridApi = params.api;
        this.registerParentContext();
    }

    getColumnDefs(): ColDef[] {
        return [
            ...(this.flags.getByFlag(FeatureFlags.enableSellerToolsDelete)
                ? [
                      {
                          headerName: '',
                          checkboxSelection: true,
                          headerCheckboxSelection: true,
                          width: 50
                      }
                  ]
                : []),
            {
                headerName: this.TR.key(
                    'seller-tools-panel.prospect-demos.tab-table.account-col'
                ),
                field: 'accountName',
                sortable: true,
                suppressMovable: true,
                cellRenderer: null,
                flex: 1
            },
            {
                headerName: this.TR.key(
                    'seller-tools-panel.tab-table.active-col'
                ),
                suppressMovable: true,
                field: 'lastActive',
                sortable: true,
                width: 150,
                cellRenderer: null,
                comparator: (valueA, valueB) =>
                    this.demoAccount.lastActiveComparator(valueA, valueB)
            },
            {
                headerName: '',
                width: 50,
                cellStyle: {
                    paddingLeft: '0',
                    paddingRight: '0'
                },
                cellRenderer: ActionMenuComponent,
                suppressMovable: true
            }
        ];
    }

    showAddProspectDemo() {
        this.showAddProspectDemoField = true;
    }

    cancel() {
        this.showAddProspectDemoField = false;
        this.saving = false;
        this.resetForm();
    }

    saveChanges() {
        const formattedProspectName =
            this.form.controls.demoAccountName.value.trim();
        this.demoAccount
            .create(formattedProspectName, this.mapSelectedToDemoUserData())
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe((response) => {
                this.saving = false;
                if (
                    response &&
                    response.validationResult ===
                        ValidationResult.DuplicateCompanyName
                ) {
                    this.form.controls.demoAccountName.setErrors({
                        duplicateDemoAccountName: true
                    });
                }

                if (
                    response &&
                    response.statusCode >= 200 &&
                    response.statusCode < 400
                ) {
                    this.showAddProspectDemoField = false;
                    this.resetForm();
                    this.demoAccount.onCreateProspectDemo();
                }
            });
    }

    private deleteAccountConfirmation(
        accountInfo: SellerToolsAccountTableData
    ) {
        this.tapestrySidenav.close();
        this.dialog.open(DeleteAccountModalComponent, {
            width: '580px',
            backdropClass: 'tapestry--overlay',
            restoreFocus: true,
            closeOnNavigation: false,
            data: accountInfo,
            exitAnimationDuration: 200
        });
    }

    private addThirdPartyUsers(accountInfo: SellerToolsAccountTableData) {
        this.tapestrySidenav.close();
        this.dialog.open(AddThirdPartyUsersModalComponent, {
            width: '580px',
            backdropClass: 'tapestry--overlay',
            restoreFocus: true,
            closeOnNavigation: true,
            data: accountInfo,
            exitAnimationDuration: 200,
            disableClose: true
        });
    }

    private registerParentContext() {
        if (this.prospectDemoAccountsGrid.gridApi) {
            this.prospectDemoAccountsGrid.gridApi.setGridOption('context', {
                deleteAccountConfirmation:
                    this.deleteAccountConfirmation.bind(this),
                addThirdPartyUsers: this.addThirdPartyUsers.bind(this)
            });
        }
    }

    private resetForm() {
        this.form.reset();
        this.form.controls.users.controls = {};
        this.form.markAsUntouched();
        this.form.markAsPristine();
        this.form.updateValueAndValidity();
    }

    private registerFormControls() {
        for (const record of this.selectedDemoUserData) {
            this.form.controls.users.addControl(
                record.surveyUserId,
                this.fb.control(true)
            );
        }
    }

    private mapSelectedToDemoUserData(): ProspectData[] {
        return Object.entries(this.form.value.users)
            .filter(([, v]) => v)
            .map(([k]) =>
                this.selectedDemoUserData.find((d) => d.surveyUserId === k)
            );
    }
}
